import { http } from './client'
import type { GitHubReposResponse, RepoMetaResponse } from '../types/api'

interface GitHubInitiateResponse {
  authorize_url: string
}

/**
 * Fetches GitHub's OAuth consent URL for the authenticated tenant (DEC-091).
 * This is a normal authenticated fetch (unlike the full-page navigation it
 * replaced) — the backend verifies the caller's JWT before returning
 * anything, since initiating a connection for a tenant now requires being
 * authenticated as that tenant.
 *
 * Callers should navigate the browser to the returned `authorize_url`
 * directly (`window.location.href = authorize_url`) — it points straight at
 * GitHub, not back through our own backend.
 *
 * `returnTo` is the SPA path to land back on once the callback completes
 * (DEC-090) — pass the current page's path (e.g. `window.location.pathname`).
 */
export async function initiateGithubConnect(tenantId: string, returnTo: string): Promise<string> {
  const { authorize_url } = await http.post<GitHubInitiateResponse>('/auth/github/initiate', {
    tenant_id: tenantId,
    return_to: returnTo,
  })
  return authorize_url
}

export const githubApi = {
  listRepos: (): Promise<GitHubReposResponse> => http.get('/auth/github/repos'),

  disconnect: (): Promise<void> => http.delete('/auth/github/token'),

  /** DEC-176. Default branch and branch list for a pasted repository URL. */
  repoMeta: (repoUrl: string): Promise<RepoMetaResponse> =>
    http.get(`/api/v1/github/repo-meta?repo_url=${encodeURIComponent(repoUrl)}`),
}

// Maps the backend's short `?github_error=` codes to human copy (DEC-090,
// UI_SDD §R.5) — the backend never sends a raw message, only a fixed code.
// Shared by ConnectPage.tsx and SettingsPage.tsx (DEC-096) — both are valid
// entry points for connecting GitHub.
const GITHUB_ERROR_COPY: Record<string, string> = {
  denied: 'You cancelled the GitHub authorization.',
  invalid_state: 'This connection link expired or was already used — try connecting again.',
  exchange_failed: "GitHub couldn't be reached to finish connecting — try again in a moment.",
}

export function githubErrorMessage(code: string): string {
  return GITHUB_ERROR_COPY[code] ?? 'Could not connect GitHub — try again.'
}

/**
 * Turns a caught error from `initiateGithubConnect` into display copy.
 *
 * DEC-095: the "not configured" case (no GitHub OAuth App registered on this
 * server at all) is a one-time, deployment-wide setup gap — never framed as
 * something a tenant admin role can fix, since no such fix path exists.
 */
export function githubConnectErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : ''
  const notConfigured = message.toLowerCase().includes('not configured')
  return notConfigured
    ? "GitHub sign-in isn't set up on this server yet — this is a one-time setup step for whoever manages this deployment, not a per-workspace setting."
    : message || 'Could not start the GitHub connection'
}
